import type { UseMutationOptions, UseMutationResult, UseSuspenseQueryOptions, UseSuspenseQueryResult } from '@tanstack/react-query';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { isAfter, isBefore, startOfToday } from 'date-fns';
import type { DirectlyObservedTherapyDto, PatientAddressDto } from '../generated/model';
import { request } from '../axios.ts';
import { getDirectlyObservedTherapy } from '../../utils/therapy.ts';

export type DirectlyObservedTherapyStatus = 'PAST' | 'ACTIVE' | 'FUTURE';

export type DirectlyObservedTherapyEntryDto = {
  id: string;
  applicant: PatientAddressDto;
  executor: PatientAddressDto;
  from: string;
  until?: string | null;
  frequenz?: string;
  status: DirectlyObservedTherapyStatus;
};

export type CreateDirectlyObservedTherapyDto = DirectlyObservedTherapyDto & {
  from: string;
  until?: string;
};

export type UpdateDirectlyObservedTherapyDto = {
  until: string;
  frequenz?: string;
};

type DirectlyObservedTherapyValue =
  | DirectlyObservedTherapyDto
  | DirectlyObservedTherapyDto[]
  | null
  | undefined;

type RequestOptions = Parameters<typeof request>[1];

function inferDirectlyObservedTherapyStatus(from: string, until?: string | null): DirectlyObservedTherapyStatus {
  const today = startOfToday();

  if (isAfter(new Date(from), today)) {
    return 'FUTURE';
  }

  if (until && isBefore(new Date(until), today)) {
    return 'PAST';
  }

  return 'ACTIVE';
}

export function mapTherapyDirectlyObservedTherapyToEntries(
  directlyObservedTherapy?: DirectlyObservedTherapyValue,
): DirectlyObservedTherapyEntryDto[] {
  const dot = getDirectlyObservedTherapy(directlyObservedTherapy);
  if (!dot) {
    return [];
  }

  const dotWithDates = dot as DirectlyObservedTherapyDto & { from?: string; until?: string | null };
  const from = dotWithDates.from ?? dot.executor?.from ?? dot.applicant?.from ?? '';
  const until = dotWithDates.until ?? dot.executor?.until ?? dot.applicant?.until;

  return [
    normalizeDirectlyObservedTherapyEntry({
      id: dot.executor?.id ?? dot.applicant?.id ?? 'legacy-dot',
      applicant: dot.applicant,
      executor: dot.executor,
      from,
      until,
      frequenz: dot.frequenz,
      status: inferDirectlyObservedTherapyStatus(from, until),
    }),
  ];
}

function normalizeDirectlyObservedTherapyEntry(entry: DirectlyObservedTherapyEntryDto): DirectlyObservedTherapyEntryDto {
  const from = entry.from || entry.executor?.from || entry.applicant?.from || '';
  const until = entry.until ?? entry.executor?.until ?? entry.applicant?.until;
  const status = entry.status ?? inferDirectlyObservedTherapyStatus(from, until);

  return {
    ...entry,
    from,
    until,
    status,
  };
}

export async function getDirectlyObservedTherapyByTherapy(
  therapyId: string,
  options?: RequestOptions,
  signal?: AbortSignal,
) {
  if (!therapyId) {
    return [];
  }

  try {
    const entries = await request<DirectlyObservedTherapyEntryDto[]>(
      {
        url: `/api/patients/therapy/${therapyId}/directly-observed-therapy`,
        method: 'GET',
        signal,
      },
      options,
    );

    return entries.map(normalizeDirectlyObservedTherapyEntry);
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) {
      return [];
    }

    throw error;
  }
}

export function getDirectlyObservedTherapyByTherapyQueryKey(therapyId: string) {
  return [`/api/patients/therapy/${therapyId}/directly-observed-therapy`] as const;
}

export function useGetDirectlyObservedTherapyByTherapySuspense(
  therapyId: string,
  options?: {
    query?: Partial<UseSuspenseQueryOptions<DirectlyObservedTherapyEntryDto[]>>;
    request?: RequestOptions;
  },
): UseSuspenseQueryResult<DirectlyObservedTherapyEntryDto[]> & {
  queryKey: ReturnType<typeof getDirectlyObservedTherapyByTherapyQueryKey>;
} {
  const queryKey = getDirectlyObservedTherapyByTherapyQueryKey(therapyId);

  const query = useSuspenseQuery({
    queryKey,
    queryFn: ({ signal }) => getDirectlyObservedTherapyByTherapy(therapyId, options?.request, signal),
    ...options?.query,
  }) as UseSuspenseQueryResult<DirectlyObservedTherapyEntryDto[]> & {
    queryKey: ReturnType<typeof getDirectlyObservedTherapyByTherapyQueryKey>;
  };

  query.queryKey = queryKey;
  return query;
}

export function createDirectlyObservedTherapyByPatient(
  patientId: string,
  data: CreateDirectlyObservedTherapyDto,
  options?: RequestOptions,
) {
  return request<void>(
    {
      url: `/api/patients/${patientId}/therapy/directly-observed-therapy`,
      method: 'POST',
      data,
    },
    options,
  );
}

export function useCreateDirectlyObservedTherapyByPatient(
  options?: UseMutationOptions<void, Error, { patientId: string; data: CreateDirectlyObservedTherapyDto }>,
): UseMutationResult<void, Error, { patientId: string; data: CreateDirectlyObservedTherapyDto }> {
  return useMutation({
    mutationFn: ({ patientId, data }) => createDirectlyObservedTherapyByPatient(patientId, data),
    ...options,
  });
}

export function updateDirectlyObservedTherapyByPatient(
  patientId: string,
  dotId: string,
  data: UpdateDirectlyObservedTherapyDto,
  options?: RequestOptions,
) {
  return request<void>(
    {
      url: `/api/patients/${patientId}/therapy/directly-observed-therapy/${dotId}`,
      method: 'PUT',
      data,
    },
    options,
  );
}

export function useUpdateDirectlyObservedTherapyByPatient(
  options?: UseMutationOptions<
    void,
    Error,
    { patientId: string; dotId: string; data: UpdateDirectlyObservedTherapyDto }
  >,
): UseMutationResult<void, Error, { patientId: string; dotId: string; data: UpdateDirectlyObservedTherapyDto }> {
  return useMutation({
    mutationFn: ({ patientId, dotId, data }) => updateDirectlyObservedTherapyByPatient(patientId, dotId, data),
    ...options,
  });
}
